import React from "react";
import Select from "react-select";
import { HiMiniXMark } from "react-icons/hi2";
import { IoSearchOutline } from "react-icons/io5";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import MixFilter from "./MixFilter"; // Your MixFilter component
import {
  CustomDropdownIndicator,
  RowsPerPageSelect,
} from "../pages/SchoolList";

interface SearchFilterBarProps {
  refetchSchools: (params?: any) => Promise<any>;
  itemsPerRow: number;
  setItemsPerRow: (value: number) => void;
  setCurrentPage: (page: number) => void;

  searchText: string;
  setSearchText: (text: string) => void;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
  filters: {
    trustee: { value: string; name: string } | null;
    institute: { value: string; name: string } | null;
  };
  setFilters: (filters: any) => void;

  setLoading: (loading: boolean) => void;
  refetchLoading: boolean;
  setRefetchLoading: (loading: boolean) => void;
  showContainerBorder?: boolean;
  containerBgColor?: string;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  refetchSchools,
  itemsPerRow,
  setItemsPerRow,
  setCurrentPage,
  searchText,
  setSearchText,
  searchFilter,
  setSearchFilter,
  filters,
  setFilters,
  setLoading,
  refetchLoading,
  setRefetchLoading,
  showContainerBorder = true,
  containerBgColor = "bg-white",
}) => {
  const handleSearch = async () => {
    if (searchText.trim() === "") {
      handleClearSearch();
      return;
    }

    setRefetchLoading(true);
    setCurrentPage(1);

    const params = buildRefetchParams(1);
    try {
      await refetchSchools(params);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setRefetchLoading(false);
    }
  };

  const handleClearSearch = async () => {
    setSearchText("");
    setRefetchLoading(true);
    setCurrentPage(1);

    try {
      const params = buildRefetchParams(1);
      params.trustee_id = filters.trustee?.value || null;
      params.school_id = filters.institute?.value || null;
      params.school_name = null;
      await refetchSchools(params);
    } catch (error) {
      console.error("Clear search failed:", error);
    } finally {
      setRefetchLoading(false);
    }
  };

  const handleItemsPerRowChange = async (value: number) => {
    setCurrentPage(1);
    setItemsPerRow(value);
    setRefetchLoading(true);

    try {
      const params = buildRefetchParams(1);
      params.limit = value;
      await refetchSchools(params);
    } catch (error) {
      console.error("Items per row change failed:", error);
    } finally {
      setRefetchLoading(false);
    }
  };

  const handleTrusteeFilterChange = async (selected: any) => {
    const newFilters = {
      ...filters,
      trustee: selected,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    setRefetchLoading(true);

    const params = buildRefetchParams(1);
    params.trustee_id = selected?.value;

    try {
      await refetchSchools(params);
    } catch (error) {
      console.error("Trustee filter change failed:", error);
    } finally {
      setRefetchLoading(false);
    }
  };

  const handleSchoolFilterChange = async (selected: any) => {
    const newFilters = {
      ...filters,
      institute: selected,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    setRefetchLoading(true);
    const params = buildRefetchParams(1);
    params.school_id = selected?.value;

    try {
      await refetchSchools(params);
    } catch (error) {
      console.error("School filter change failed:", error);
    } finally {
      setRefetchLoading(false);
    }
  };

  const handleClearTrusteeFilter = async () => {
    const newFilters = {
      ...filters,
      trustee: null,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    setRefetchLoading(true);

    const params = buildRefetchParams(1);
    params.trustee_id = null;

    try {
      await refetchSchools(params);
    } catch (error) {
      console.error("Clear trustee filter failed:", error);
    } finally {
      setRefetchLoading(false);
    }
  };

  const handleClearInstituteFilter = async () => {
    const newFilters = {
      ...filters,
      institute: null,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    setRefetchLoading(true);
    const params = buildRefetchParams(1);
    params.school_id = null;

    try {
      await refetchSchools(params);
    } catch (error) {
      console.error("Clear institute filter failed:", error);
    } finally {
      setRefetchLoading(false);
    }
  };

  const handleFilterCancel = async () => {
    const newFilters = {
      trustee: null,
      institute: null,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    setRefetchLoading(true);
    const params = buildRefetchParams(1);
    if (searchText.trim() !== "") {
      if (searchFilter === "school_id") {
        params.school_id = searchText.trim();
      } else if (searchFilter === "school_name") {
        params.school_name = searchText.trim();
      } else if (searchFilter === "trustee_id") {
        params.trustee_id = searchText.trim();
      }
    } else {
      params.school_id = null;
      params.trustee_id = null;
    }
    try {
      await refetchSchools(params);
    } catch (error) {
      console.error("Filter cancel failed:", error);
    } finally {
      setRefetchLoading(false);
    }
  };

  const buildRefetchParams = (page: number) => {
    const params: any = {
      page,
      limit: itemsPerRow,
    };

    if (searchText.trim() !== "") {
      if (searchFilter === "school_id") {
        params.school_id = searchText.trim();
      } else if (searchFilter === "school_name") {
        params.school_name = searchText.trim();
      } else if (searchFilter === "trustee_id") {
        params.trustee_id = searchText.trim();
      }
    }

    if (filters) {
      if (filters.trustee && !(searchFilter === "trustee_id")) {
        params.trustee_id = filters.trustee?.value;
      }
      if (filters.institute && !(searchFilter === "school_id")) {
        params.school_id = filters.institute?.value;
      }
    }

    return params;
  };

  const handleFilterApply = () => {
    if (filters.trustee) {
      handleTrusteeFilterChange(filters.trustee);
    }
    if (filters.institute) {
      handleSchoolFilterChange(filters.institute);
    }
  };
  const hasActiveFilters = filters.trustee || filters.institute || searchText;

  const handleClearAllFilters = async () => {
    if (!hasActiveFilters) return;

    setSearchText("");
    setSearchFilter("school_id");

    setFilters({
      trustee: null,
      institute: null,
    });

    setCurrentPage(1);

    setRefetchLoading(true);
    const params = buildRefetchParams(1);
    try {
      params.school_id = null;
      params.trustee_id = null;
      params.school_name = null;
      await refetchSchools(params);
    } catch (error) {
      console.error("Clear all filters failed:", error);
    } finally {
      setRefetchLoading(false);
    }
  };

  const containerClass = `
    mb-6 rounded-lg p-4 
    ${containerBgColor} 
    ${showContainerBorder ? "border border-gray-200" : ""}
  `;
  return (
    <div className={containerClass}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <div className="flex items-center border border-gray-300 rounded-lg px-3 py-3 w-full md:w-3/5 lg:w-2/5">
          <input
            className="text-sm pr-2 bg-transparent focus:outline-none w-full placeholder:font-normal"
            type="text"
            value={searchText}
            placeholder="Search (School ID, School Name...)"
            onChange={(e) => setSearchText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          {searchText.length > 0 && (
            <HiMiniXMark
              onClick={handleClearSearch}
              className="text-[#1E1B59] cursor-pointer text-md mr-2 shrink-0"
            />
          )}
          <Select
            className="border-l-2 border-gray-400"
            options={[
              { label: "School ID", value: "school_id" },
              { label: "School Name", value: "school_name" },
              { label: "Trustee Id", value: "trustee_id" },
            ]}
            isSearchable={false}
            components={{
              DropdownIndicator: CustomDropdownIndicator,
              IndicatorSeparator: () => null,
            }}
            onChange={(option: any) => {
              setSearchFilter(option.value);
            }}
            value={
              searchFilter
                ? {
                    label: searchFilter
                      .toString()
                      .toLowerCase()
                      .replace(/_/g, " ")
                      .replace(/^\w/, (c) => c.toUpperCase())
                      .replace(/\b\w/g, (c) => c.toUpperCase()),
                    value: searchFilter,
                  }
                : null
            }
            styles={{
              control: (provided) => ({
                ...provided,
                backgroundColor: "transparent",
                height: "28px",
                border: "none",
                boxShadow: "none",
                cursor: "pointer",
                minHeight: "28px",
                padding: "0px",
              }),
              valueContainer: (provided) => ({
                ...provided,
                height: "28px",
                width: "9rem",
                padding: "0 8px",
              }),
              singleValue: (provided) => ({
                ...provided,
                lineHeight: "28px",
                fontSize: "12px",
                color: "#4F46E5",
              }),
              indicatorsContainer: (provided) => ({
                ...provided,
                height: "28px",
              }),
              option: (provided, state) => ({
                ...provided,
                fontSize: "12px",
                cursor: "pointer",
                padding: "10px 12px",
                backgroundColor: state.isSelected
                  ? "#6366F1"
                  : state.isFocused
                  ? "#F5F3FF"
                  : "white",
                color: state.isSelected ? "white" : "#374151",
                "&:hover": {
                  backgroundColor: state.isSelected ? "#6366F1" : "#F5F3FF",
                  color: state.isSelected ? "white" : "#374151",
                },
              }),
              menu: (provided) => ({
                ...provided,
                zIndex: 9999,
                position: "absolute",
                backgroundColor: "white",
                minWidth: "180px",
                width: "auto",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }),
              menuList: (provided) => ({
                ...provided,
                maxHeight: "250px",
                padding: "4px 0",
              }),
            }}
          />
          <div className="w-10 z-50 shrink-0 flex justify-center items-center">
            {searchText.length > 0 && refetchLoading ? (
              <AiOutlineLoading3Quarters className="text-sm animate-spin" />
            ) : (
              <IoSearchOutline
                onClick={handleSearch}
                className="cursor-pointer text-edvion_black text-opacity-50 text-md"
              />
            )}
          </div>
        </div>

        {/* MixFilter at right end */}
        <div className="flex justify-end md:w-auto w-full gap-2">
          {" "}
          {/* Added gap-2 */}
          <div className="w-48 relative">
            <MixFilter
              filters={filters}
              setFilters={setFilters}
              onApply={handleFilterApply}
              onCancel={handleFilterCancel}
            />
          </div>
          <div className="w-48 relative ">
            <button
              onClick={handleClearAllFilters}
              className="focus:outline-none border border-edviron_black hover:border-gray-300 transition-all duration-150 rounded-md py-3 px-4 w-full text-sm text-left flex items-center justify-between min-h-[44px] bg-white hover:bg-gray-50"
            >
              <span className="mr-2">Clear All Filters</span>
              <HiMiniXMark className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <RowsPerPageSelect
          setItemsPerRow={handleItemsPerRowChange}
          itemsPerRow={itemsPerRow}
          className="justify-start"
        />
      </div>

      {/* Active filters display */}
      <div className="flex flex-wrap mt-3">
        {filters.trustee && (
          <div className="bg-[#6687FFCC] text-sm m-2 rounded-lg min-w-max h-10 px-3 flex items-center gap-x-2">
            <span className="text-white capitalize">
              Trustee: {filters.trustee.name}
            </span>
            <HiMiniXMark
              className="text-white cursor-pointer h-4 w-4"
              onClick={handleClearTrusteeFilter}
            />
          </div>
        )}
        {filters.institute && (
          <div className="bg-[#6687FFCC] text-sm m-2 rounded-lg min-w-max h-10 px-3 flex items-center gap-x-2">
            <span className="text-white capitalize">
              Institute: {filters.institute.name}
            </span>
            <HiMiniXMark
              className="text-white cursor-pointer h-4 w-4"
              onClick={handleClearInstituteFilter}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchFilterBar;
